import { useLayoutEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { ContactShadows, OrbitControls } from "@react-three/drei";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Layers, MousePointer2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import SectionHeading from "@/components/SectionHeading";

const W = 8;
const D = 5;
const PEAK = 2.6;
const HALF_D = D / 2;
const RL = (W - D) / 2;
const SLOPE = Math.hypot(PEAK, HALF_D);
const THETA = Math.atan2(PEAK, HALF_D);
const NX = HALF_D / SLOPE;
const NY = PEAK / SLOPE;
const WOOD = "#C9A876";
const WOOD_DARK = "#B08E5E";

export const MATERIALS = [
  {
    id: "tuile",
    name: "Tuile canal",
    desc: "La signature des toits bordelais. Terre cuite du Sud-Ouest posée en pureau, chaleureuse et increvable.",
    points: ["Terre cuite naturelle", "40 à 100 ans de durée de vie", "Esprit Sud-Ouest authentique"],
  },
  {
    id: "ardoise",
    name: "Ardoise",
    desc: "Le raffinement absolu des hôtels particuliers. Ardoise d’Espagne posée au clou, rangs parfaitement alignés.",
    points: ["Jusqu’à 100 ans", "Pose au clou, crochets inox", "Finitions au cordeau"],
  },
  {
    id: "zinc",
    name: "Zinc à joint debout",
    desc: "L’esprit haussmannien des immeubles du centre. Léger, durable et façonné à la main dans notre atelier.",
    points: ["50 ans sans entretien", "100 % recyclable", "Joints debout façonnés main"],
  },
  {
    id: "plat",
    name: "Toiture-terrasse",
    desc: "Étanchéité EPDM ou bitume pour extensions et toits plats, avec finition zinc et vélum sur mesure.",
    points: ["Membrane EPDM soudée", "Isolation sarking intégrée", "Accessible ou végétalisable"],
  },
];

function useInstances(ref, transforms, colors) {
  useLayoutEffect(() => {
    if (!ref.current) return;
    const m = new THREE.Matrix4();
    transforms.forEach((t, i) => {
      m.compose(t.position, t.quaternion, t.scale || new THREE.Vector3(1, 1, 1));
      ref.current.setMatrixAt(i, m);
      if (colors) ref.current.setColorAt(i, colors[i]);
    });
    ref.current.instanceMatrix.needsUpdate = true;
    if (ref.current.instanceColor) ref.current.instanceColor.needsUpdate = true;
  }, [ref, transforms, colors]);
}

const basisQuat = (yAxis, zAxis) => {
  const x = new THREE.Vector3().crossVectors(yAxis, zAxis).normalize();
  return new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, yAxis, zAxis));
};
const upEnd = (side) => new THREE.Vector3(-side * NX, NY, 0);
const downEnd = (side) => new THREE.Vector3(side * NX, -NY, 0);
const normalEnd = (side) => new THREE.Vector3(side * NY, NX, 0);
const endQuat = (side) => basisQuat(normalEnd(side), downEnd(side));

function buildCovering({ hip, rowStep, rowStart, tileW, lift, colorFn, stagger }) {
  const transforms = [];
  const colors = [];
  for (const side of [1, -1]) {
    const qMain = new THREE.Quaternion().setFromEuler(new THREE.Euler(side * THETA, 0, 0));
    const qEnd = hip ? endQuat(side) : null;
    for (let r = 0; rowStart + r * rowStep < SLOPE - 0.12; r++) {
      const s = rowStart + r * rowStep;
      const y = s * NY;
      const halfMain = hip ? W / 2 - s * NX - 0.06 : W / 2 - 0.08;
      const off = stagger && r % 2 ? tileW / 2 : 0;
      const zp = HALF_D - s * NX + lift * NX;
      const pushTile = (cx, sx) => {
        if (!hip && side === 1 && Math.abs(cx - 1.15) < 0.72 && zp > 1.12 && zp < 2.02) return;
        const p = new THREE.Vector3(cx, y + lift * NY, side * zp);
        const q = qMain.clone();
        if (stagger) q.multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, (Math.random() - 0.5) * 0.045)));
        transforms.push({ position: p, quaternion: q, ...(sx !== 1 ? { scale: new THREE.Vector3(sx, 1, 1) } : {}) });
        if (colorFn) colors.push(colorFn());
      };
      if (off > 0) pushTile(-halfMain + off / 2, off / tileW);
      for (let x = -halfMain + tileW / 2 + off; x - tileW / 2 < halfMain - 0.001; x += tileW) {
        const right = x + tileW / 2;
        if (right > halfMain) {
          const w = halfMain - (x - tileW / 2);
          if (w < 0.12) break;
          pushTile(x - tileW / 2 + w / 2, w / tileW);
          break;
        }
        pushTile(x, 1);
      }
      if (!hip) continue;
      const halfEnd = HALF_D - s * NX - 0.06;
      const n = normalEnd(side);
      const pushEnd = (cz, sz) => {
        const p = new THREE.Vector3(side * (W / 2 - s * NX) + n.x * lift, y + n.y * lift, cz);
        transforms.push({ position: p, quaternion: qEnd.clone(), ...(sz !== 1 ? { scale: new THREE.Vector3(sz, 1, 1) } : {}) });
        if (colorFn) colors.push(colorFn());
      };
      if (off > 0) pushEnd(-halfEnd + off / 2, off / tileW);
      for (let z = -halfEnd + tileW / 2 + off; z - tileW / 2 < halfEnd - 0.001; z += tileW) {
        const right = z + tileW / 2;
        if (right > halfEnd) {
          const w = halfEnd - (z - tileW / 2);
          if (w < 0.12) break;
          pushEnd(z - tileW / 2 + w / 2, w / tileW);
          break;
        }
        pushEnd(z, 1);
      }
    }
  }
  return { transforms, colors };
}

const beamBetween = (ax, ay, az, bx, by, bz, w, h = w) => {
  const a = new THREE.Vector3(ax, ay, az);
  const b = new THREE.Vector3(bx, by, bz);
  const dir = new THREE.Vector3().subVectors(b, a);
  const len = dir.length();
  const pos = new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5);
  const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  return { pos: [pos.x, pos.y, pos.z], size: [w, len, h], quaternion };
};

function RidgeCaps({ color, hip }) {
  const caps = useMemo(() => {
    if (!hip) return [];
    const arr = [];
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        arr.push(beamBetween(sx * (W / 2), 0.1, sz * HALF_D, sx * RL, PEAK + 0.09, 0, 0.24, 0.2));
      }
    }
    return arr;
  }, [hip]);
  return (
    <group>
      <mesh position={[0, PEAK + 0.08, 0]}>
        <boxGeometry args={[(hip ? RL * 2 : W) + 0.4, 0.09, 0.42]} />
        <meshStandardMaterial color={color} roughness={0.6} />
      </mesh>
      {caps.map((c, i) => (
        <mesh key={i} position={c.pos} quaternion={c.quaternion}>
          <boxGeometry args={c.size} />
          <meshStandardMaterial color={color} roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

function RidgeEnds({ color, hip }) {
  const pieces = useMemo(() => {
    const arr = [];
    if (hip) {
      for (const sx of [1, -1]) {
        arr.push({ pos: [sx * RL, PEAK + 0.075, 0], size: [0.44, 0.09, 0.44], quaternion: null });
        for (const sz of [1, -1]) {
          arr.push(beamBetween(sx * (RL - 0.15), PEAK + 0.09, 0, sx * (RL + 0.3), PEAK - 0.24, sz * 0.3, 0.24, 0.14));
        }
      }
    } else {
      for (const sx of [1, -1]) {
        arr.push({ pos: [sx * (W / 2 + 0.08), PEAK + 0.08, 0], size: [0.36, 0.14, 0.5], quaternion: null });
        arr.push({ pos: [sx * (W / 2 + 0.22), PEAK - 0.05, 0], size: [0.09, 0.34, 0.56], quaternion: null });
        for (const side of [1, -1]) {
          arr.push({
            pos: [sx * (W / 2 + 0.04), PEAK - 0.27 + 0.05 * NY, side * (0.3 + 0.05 * NX)],
            size: [0.38, 0.08, 0.62],
            quaternion: new THREE.Quaternion().setFromEuler(new THREE.Euler(side * THETA, 0, 0)),
          });
        }
      }
    }
    return arr;
  }, [hip]);
  return (
    <group>
      {pieces.map((p, i) => (
        <mesh key={i} position={p.pos} {...(p.quaternion ? { quaternion: p.quaternion } : {})}>
          <boxGeometry args={p.size} />
          <meshStandardMaterial color={color} roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

function Rivages({ color, hip }) {
  const boards = useMemo(() => {
    const arr = [];
    for (const side of [1, -1]) {
      arr.push({ pos: [0, 0.05, side * (HALF_D + 0.1)], size: [W + 0.34, 0.18, 0.07], quaternion: null });
      if (hip) {
        arr.push({ pos: [side * (W / 2 + 0.1), 0.05, 0], size: [0.07, 0.18, D + 0.2], quaternion: null });
      }
    }
    if (!hip) {
      for (const sx of [1, -1]) {
        for (const sz of [1, -1]) {
          arr.push(beamBetween(sx * (W / 2 + 0.06), 0.02, sz * HALF_D, sx * (W / 2 + 0.06), PEAK + 0.06, 0, 0.07, 0.18));
        }
      }
    }
    return arr;
  }, [hip]);
  return (
    <group>
      {boards.map((b, i) => (
        <mesh key={i} position={b.pos} {...(b.quaternion ? { quaternion: b.quaternion } : {})}>
          <boxGeometry args={b.size} />
          <meshStandardMaterial color={color} roughness={0.55} />
        </mesh>
      ))}
    </group>
  );
}

function Tuiles({ hip }) {
  const ref = useRef();
  const { transforms, colors } = useMemo(() => {
    const color = new THREE.Color();
    return buildCovering({
      hip,
      rowStep: 0.42,
      rowStart: 0.235,
      tileW: 0.44,
      lift: 0.06,
      stagger: true,
      colorFn: () => {
        color.setHSL(0.04 + Math.random() * 0.02, 0.52 + Math.random() * 0.16, 0.3 + Math.random() * 0.11);
        return color.clone();
      },
    });
  }, [hip]);
  const geo = useMemo(() => {
    const g = new THREE.CylinderGeometry(0.23, 0.23, 0.5, 10, 1, true, 0, Math.PI);
    g.rotateZ(Math.PI / 2);
    g.rotateY(Math.PI / 2);
    return g;
  }, []);
  useInstances(ref, transforms, colors);
  return (
    <group>
      <instancedMesh ref={ref} args={[geo, undefined, transforms.length]} frustumCulled={false}>
        <meshStandardMaterial roughness={0.65} metalness={0.05} side={THREE.DoubleSide} />
      </instancedMesh>
      <RidgeCaps color="#A34A26" hip={hip} />
      <Rivages color="#8F3F1F" hip={hip} />
      <RidgeEnds color="#A34A26" hip={hip} />
      {!hip && <Lucarne color="#A34A26" />}
    </group>
  );
}

function Ardoises({ hip }) {
  const ref = useRef();
  const { transforms, colors } = useMemo(() => {
    const color = new THREE.Color();
    return buildCovering({
      hip,
      rowStep: 0.3,
      rowStart: 0.225,
      tileW: 0.335,
      lift: hip ? 0.05 : 0.1,
      stagger: true,
      colorFn: () => {
        color.setHSL(0.58, 0.12 + Math.random() * 0.08, 0.15 + Math.random() * 0.08);
        return color.clone();
      },
    });
  }, [hip]);
  useInstances(ref, transforms, colors);
  return (
    <group>
      <instancedMesh ref={ref} args={[undefined, undefined, transforms.length]} frustumCulled={false}>
        <boxGeometry args={[0.34, 0.03, 0.45]} />
        <meshStandardMaterial roughness={0.55} metalness={0.25} />
      </instancedMesh>
      <RidgeCaps color="#232B33" hip={hip} />
      <Rivages color="#1E262E" hip={hip} />
      <RidgeEnds color="#232B33" hip={hip} />
      {!hip && <Lucarne color="#232B33" />}
    </group>
  );
}

function Zinc({ hip }) {
  const seamRef = useRef();
  const panMain = useMemo(() => {
    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.lineTo(W, 0);
    shape.lineTo(W / 2 + RL, SLOPE);
    shape.lineTo(W / 2 - RL, SLOPE);
    shape.closePath();
    const g = new THREE.ExtrudeGeometry(shape, { depth: 0.05, bevelEnabled: false });
    g.translate(0, 0, -0.025);
    return g;
  }, []);
  const panEnd = useMemo(() => {
    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.lineTo(D, 0);
    shape.lineTo(D / 2, SLOPE);
    shape.closePath();
    const g = new THREE.ExtrudeGeometry(shape, { depth: 0.05, bevelEnabled: false });
    g.translate(0, 0, -0.025);
    return g;
  }, []);

  const pans = useMemo(() => {
    if (!hip) return [];
    const arr = [];
    for (const side of [1, -1]) {
      const y = new THREE.Vector3(0, NY, -side * NX);
      const x = new THREE.Vector3(1, 0, 0);
      const z = new THREE.Vector3().crossVectors(x, y).normalize();
      arr.push({
        geo: "main",
        position: [-W / 2, 0.04 * NX, side * (HALF_D + 0.04 * NY)],
        quaternion: new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z)),
      });
      const xe = new THREE.Vector3(0, 0, 1);
      const ye = upEnd(side);
      const ze = new THREE.Vector3().crossVectors(xe, ye).normalize();
      const n = normalEnd(side);
      arr.push({
        geo: "end",
        position: [side * (W / 2) + n.x * 0.04, n.y * 0.04, -HALF_D],
        quaternion: new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(xe, ye, ze)),
      });
    }
    return arr;
  }, [hip]);

  const seams = useMemo(() => {
    const arr = [];
    const seamLift = hip ? 0.09 : 0.15;
    for (const side of [1, -1]) {
      const qm = new THREE.Quaternion().setFromEuler(new THREE.Euler(side * THETA, 0, 0));
      for (let x = -3.7; x <= 3.7; x += 0.53) {
        const maxS = hip ? Math.min(SLOPE, (W / 2 - Math.abs(x)) / NX) : SLOPE;
        if (maxS < 0.7) continue;
        if (!hip && side === 1 && Math.abs(x - 1.15) < 0.6) continue;
        const len = maxS - 0.05;
        const sm = 0.05 + len / 2;
        arr.push({
          position: new THREE.Vector3(x, sm * NY + seamLift * NX, side * (HALF_D - sm * NX + seamLift * NY)),
          quaternion: qm,
          scale: new THREE.Vector3(1, 1, len),
        });
      }
      if (!hip) continue;
      const qe = endQuat(side);
      for (let z = -2.1; z <= 2.1; z += 0.53) {
        const maxS = SLOPE * (1 - Math.abs(z) / HALF_D);
        if (maxS < 0.7) continue;
        const len = maxS - 0.05;
        const sm = 0.05 + len / 2;
        const n = normalEnd(side);
        arr.push({
          position: new THREE.Vector3(side * (W / 2 - sm * NX) + n.x * 0.09, sm * NY + n.y * 0.09, z),
          quaternion: qe,
          scale: new THREE.Vector3(1, 1, len),
        });
      }
    }
    return arr;
  }, [hip]);
  useInstances(seamRef, seams, null);

  return (
    <group>
      <group key={hip ? "zinc-hip" : "zinc-gable"}>
        {hip
          ? pans.map((p, i) => (
              <mesh key={i} geometry={p.geo === "main" ? panMain : panEnd} position={p.position} quaternion={p.quaternion}>
                <meshStandardMaterial color="#AEB6BE" metalness={0.55} roughness={0.42} side={THREE.DoubleSide} />
              </mesh>
            ))
          : [1, -1].map((side) => (
              <mesh
                key={side}
                position={[0, PEAK / 2 + 0.11 * NY, side * (HALF_D / 2 + 0.11 * NX)]}
                rotation={[side * THETA, 0, 0]}
              >
                <boxGeometry args={[W - 0.02, 0.05, SLOPE + 0.2]} />
                <meshStandardMaterial color="#AEB6BE" metalness={0.45} roughness={0.5} />
              </mesh>
            ))}
      </group>
      <instancedMesh ref={seamRef} args={[undefined, undefined, seams.length]} frustumCulled={false}>
        <boxGeometry args={[0.035, 0.06, 1]} />
        <meshStandardMaterial color="#C9D0D7" metalness={0.6} roughness={0.35} />
      </instancedMesh>
      <RidgeCaps color="#C9D0D7" hip={hip} />
      <Rivages color="#B9C1C9" hip={hip} />
      <RidgeEnds color="#C9D0D7" hip={hip} />
      {!hip && <Lucarne color="#C9D0D7" />}
    </group>
  );
}

function Plat() {
  return (
    <group position={[0, -1.0, 0]}>
      <mesh position={[0, 1.7, 0]}>
        <boxGeometry args={[8.4, 0.16, 5.4]} />
        <meshStandardMaterial color="#26292F" roughness={0.95} />
      </mesh>
      {[
        [0, 1.82, 2.68, 8.5, 0.1, 0.08],
        [0, 1.82, -2.68, 8.5, 0.1, 0.08],
        [4.2, 1.82, 0, 0.08, 0.1, 5.4],
        [-4.2, 1.82, 0, 0.08, 0.1, 5.4],
      ].map((b, i) => (
        <mesh key={i} position={[b[0], b[1], b[2]]}>
          <boxGeometry args={[b[3], b[4], b[5]]} />
          <meshStandardMaterial color="#9AA3AB" metalness={0.6} roughness={0.4} />
        </mesh>
      ))}
      <mesh position={[1.6, 1.86, 0.8]}>
        <boxGeometry args={[1.3, 0.16, 1.3]} />
        <meshStandardMaterial color="#8FC7E8" metalness={0.15} roughness={0.08} transparent opacity={0.75} />
      </mesh>
    </group>
  );
}

function buildHipBeams() {
  const beams = [];
  for (const side of [1, -1]) {
    beams.push(beamBetween(-W / 2 - 0.15, 0.06, side * HALF_D, W / 2 + 0.15, 0.06, side * HALF_D, 0.14, 0.12));
    beams.push(beamBetween(side * (W / 2), 0.06, -HALF_D - 0.15, side * (W / 2), 0.06, HALF_D + 0.15, 0.14, 0.12));
  }
  for (let x = -3.6; x <= 3.61; x += 0.6) {
    beams.push(beamBetween(x, 0.16, -HALF_D, x, 0.16, HALF_D, 0.1, 0.12));
  }
  for (const sx of [1, -1]) {
    for (const sz of [1, -1]) {
      beams.push(beamBetween(sx * (W / 2), 0.08, sz * HALF_D, sx * RL, PEAK + 0.04, 0, 0.13, 0.15));
    }
  }
  beams.push(beamBetween(-RL, PEAK, 0, RL, PEAK, 0, 0.14, 0.12));
  for (const x of [-RL, 0, RL]) {
    beams.push(beamBetween(x, 0.2, 0, x, PEAK - 0.08, 0, 0.11, 0.12));
  }
  for (const side of [1, -1]) {
    for (let x = -3.7; x <= 3.71; x += 0.62) {
      let top;
      if (Math.abs(x) <= RL + 0.01) {
        top = [x, PEAK, 0];
      } else {
        const u = (W / 2 - Math.abs(x)) / (W / 2 - RL);
        top = [x, PEAK * u, HALF_D * (1 - u)];
      }
      beams.push(beamBetween(x, 0.1, side * HALF_D, top[0], top[1], side * top[2], 0.09, 0.11));
    }
  }
  for (const side of [1, -1]) {
    for (let z = -2.2; z <= 2.21; z += 0.55) {
      const u = (HALF_D - Math.abs(z)) / HALF_D;
      beams.push(beamBetween(side * (W / 2), 0.1, z, side * (RL + Math.abs(z)), PEAK * u, z, 0.09, 0.11));
    }
  }
  for (const sx of [1, -1]) {
    for (const sz of [1, -1]) {
      beams.push(beamBetween(sx * 2.6, 0.2, sz * 1.2, sx * 2.75, 1.35, sz * 1.25, 0.09, 0.1));
    }
  }
  return beams;
}

const FERMES_X = [-3.2, -1.6, 0, 1.6, 3.2];

function buildGableBeams() {
  const beams = [];
  for (const side of [1, -1]) {
    beams.push(beamBetween(-W / 2 + 0.05, 0.0, side * (HALF_D - 0.02), W / 2 - 0.05, 0.0, side * (HALF_D - 0.02), 0.12, 0.1));
  }
  for (const tx of FERMES_X) beams.push(beamBetween(tx, 0.06, -HALF_D - 0.15, tx, 0.06, HALF_D + 0.15, 0.18, 0.2));
  for (const tx of FERMES_X) {
    beams.push(beamBetween(tx, 0.04, HALF_D - 0.06, tx, PEAK - 0.1, 0.1, 0.15, 0.15));
    beams.push(beamBetween(tx, 0.04, -HALF_D + 0.06, tx, PEAK - 0.1, -0.1, 0.15, 0.15));
  }
  for (const tx of FERMES_X) beams.push(beamBetween(tx, 0.02, 0, tx, PEAK + 0.02, 0, 0.13, 0.15));
  for (const tx of FERMES_X) {
    beams.push(beamBetween(tx, 0.12, 1.5, tx, 1.5, 1.06, 0.1, 0.11));
    beams.push(beamBetween(tx, 0.12, -1.5, tx, 1.5, -1.06, 0.1, 0.11));
  }
  beams.push(beamBetween(-W / 2 + 0.08, PEAK - 0.16, 0, W / 2 - 0.08, PEAK - 0.16, 0, 0.15, 0.13));
  for (const f of [0.35, 0.68]) {
    for (const side of [1, -1]) {
      beams.push(
        beamBetween(-W / 2 + 0.06, PEAK - f * PEAK - 0.16, side * f * HALF_D, W / 2 - 0.06, PEAK - f * PEAK - 0.16, side * f * HALF_D, 0.14, 0.12)
      );
    }
  }
  for (const side of [1, -1]) {
    for (let x = -3.75; x <= 3.76; x += 0.5) {
      if (FERMES_X.some((f) => Math.abs(f - x) < 0.2)) continue;
      beams.push(beamBetween(x, 0.03, side * (HALF_D - 0.08), x, PEAK - 0.16, side * 0.12, 0.07, 0.07));
    }
  }
  return beams;
}

function buildFlatBeams() {
  const beams = [];
  for (let i = 0; i < 11; i++) {
    beams.push({ pos: [-4 + i * 0.8, 1.52, 0], size: [0.14, 0.22, D], quaternion: null });
  }
  for (let i = 0; i < 10; i++) {
    beams.push({ pos: [-3.6 + i * 0.8, 1.52, 0], size: [0.6, 0.18, 0.12], quaternion: null, color: WOOD_DARK });
  }
  for (const side of [1, -1]) {
    beams.push({ pos: [0, 1.36, side * (HALF_D - 0.08)], size: [W + 0.3, 0.14, 0.1], quaternion: null });
  }
  return beams;
}

function Charpente({ flat, hip, progressRef }) {
  const beamRefs = useRef([]);
  const beams = useMemo(() => (flat ? buildFlatBeams() : hip ? buildHipBeams() : buildGableBeams()), [flat, hip]);

  useFrame((_, dt) => {
    const p = progressRef ? progressRef.current : 1;
    const n = beams.length;
    beamRefs.current.forEach((m, i) => {
      if (!m) return;
      const start = (i / n) * 0.78;
      const t = THREE.MathUtils.clamp((p - start) / 0.05, 0, 1);
      const s = THREE.MathUtils.damp(m.scale.y, Math.max(t, 0.0001), 9, dt);
      m.scale.set(1, s, 1);
      m.visible = s > 0.02;
    });
  });

  return (
    <group position={[0, flat ? -1.0 : -0.12, 0]}>
      {beams.map((b, i) => (
        <mesh
          key={i}
          ref={(el) => (beamRefs.current[i] = el)}
          position={b.pos}
          {...(b.quaternion ? { quaternion: b.quaternion } : {})}
        >
          <boxGeometry args={b.size} />
          <meshStandardMaterial color={b.color || WOOD} roughness={0.7} />
        </mesh>
      ))}
    </group>
  );
}

function Lucarne({ color }) {
  const cx = 1.15;
  const zF = 1.91;
  const yB = ((HALF_D - zF) / NX) * NY;
  const wallH = 0.95;
  const peakY = yB + wallH + 0.44;
  const peakZ = zF - 0.33;
  const phi = Math.atan2(0.44, 0.45);
  const rot = Math.PI / 2 - phi;
  const roofLen = Math.hypot(0.44, 0.45) + 0.12;
  const midY = (yB + wallH + peakY) / 2;
  return (
    <group>
      <mesh position={[cx, 0.95, 1.62]} rotation={[THETA, 0, 0]}>
        <boxGeometry args={[1.6, 0.03, 1.15]} />
        <meshStandardMaterial color="#10131A" roughness={0.9} />
      </mesh>
      {[1, -1].map((s) => (
        <mesh key={`j${s}`} position={[cx + s * 0.53, yB + wallH / 2, zF - 0.16]}>
          <boxGeometry args={[0.05, wallH, 0.6]} />
          <meshStandardMaterial color="#E8E2D6" roughness={0.8} />
        </mesh>
      ))}
      <mesh position={[cx, yB + wallH / 2, zF]}>
        <boxGeometry args={[1.0, wallH, 0.06]} />
        <meshStandardMaterial color="#E8E2D6" roughness={0.8} />
      </mesh>
      <mesh position={[cx, yB + wallH / 2, zF + 0.045]}>
        <boxGeometry args={[0.6, 0.62, 0.04]} />
        <meshStandardMaterial color="#16202C" metalness={0.4} roughness={0.15} />
      </mesh>
      <mesh position={[cx, yB + wallH / 2, zF + 0.07]}>
        <boxGeometry args={[0.045, 0.62, 0.02]} />
        <meshStandardMaterial color="#E8E2D6" roughness={0.8} />
      </mesh>
      <mesh position={[cx, yB + wallH / 2, zF + 0.07]}>
        <boxGeometry args={[0.6, 0.045, 0.02]} />
        <meshStandardMaterial color="#E8E2D6" roughness={0.8} />
      </mesh>
      {[1, -1].map((s) => (
        <mesh key={`r${s}`} position={[cx, midY, peakZ + s * 0.225]} rotation={[s * rot, 0, 0]}>
          <boxGeometry args={[1.18, 0.06, roofLen]} />
          <meshStandardMaterial color={color} roughness={0.6} />
        </mesh>
      ))}
      <mesh position={[cx, peakY + 0.02, peakZ]}>
        <boxGeometry args={[1.2, 0.06, 0.16]} />
        <meshStandardMaterial color={color} roughness={0.6} />
      </mesh>
    </group>
  );
}

function Covering({ material, hip }) {
  if (material === "tuile") return <Tuiles hip={hip} />;
  if (material === "ardoise") return <Ardoises hip={hip} />;
  if (material === "zinc") return <Zinc hip={hip} />;
  return <Plat />;
}

function RoofScene({ material, showFrame, hip, progressRef }) {
  const coverRef = useRef();
  useFrame((_, dt) => {
    if (!coverRef.current) return;
    const p = progressRef ? progressRef.current : 1;
    const target = showFrame ? 1.5 : p > 0.9 ? 0 : 3.5;
    coverRef.current.position.y = THREE.MathUtils.damp(coverRef.current.position.y, target, 4, dt);
  });
  return (
    <group position={[0, material === "plat" ? 0.4 : -1.2, 0]}>
      <Charpente flat={material === "plat"} hip={hip} progressRef={progressRef} />
      <group ref={coverRef}>
        <Covering material={material} hip={hip} />
      </group>
    </group>
  );
}

export default function Material3D() {
  const [mat, setMat] = useState("tuile");
  const [frame, setFrame] = useState(false);
  const [hip, setHip] = useState(true);
  const active = MATERIALS.find((m) => m.id === mat);
  const sectionRef = useRef(null);
  const progressRef = useRef(0);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start 0.9", "center 0.55"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    progressRef.current = v;
  });

  return (
    <section ref={sectionRef} id="materiaux" data-testid="materials3d-section" className="relative border-t border-white/5 py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeading
          eyebrow="Matériaux en 3D"
          title="Choisissez votre couverture, inspectez la charpente"
          copy="Faites tourner chaque toiture à 360° — 4 pans ou 2 pans, comme les échoppes bordelaises. La charpente en pin se monte poutre par poutre pendant que vous faites défiler."
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1.25fr]">
          <div className="min-w-0">
            <div className="flex flex-col gap-2.5">
              {MATERIALS.map((m) => (
                <button
                  key={m.id}
                  data-testid={`mat3d-tab-${m.id}`}
                  onClick={() => {
                    setMat(m.id);
                    if (m.id === "plat" && mat !== "plat") {
                      toast("Toiture-terrasse · finition dorée", {
                        description:
                          "Notre signature : membrane EPDM soudée et rive de zinc dorée à la feuille, façonnée main dans notre atelier bordelais. Devis sur mesure uniquement.",
                        icon: <Sparkles className="h-4 w-4 text-[#D9B64A]" />,
                        style: { background: "#191507", border: "1px solid #C9A227", color: "#F0D98C" },
                      });
                    }
                  }}
                  className={`flex items-center justify-between border p-4 text-left transition-colors duration-300 ${
                    mat === m.id
                      ? "border-terra/50 bg-terra/5"
                      : "border-white/5 bg-[#14171D] hover:border-white/20"
                  }`}
                >
                  <span className="font-serif text-base text-slate-100">{m.name}</span>
                  <span className={`h-2 w-2 rotate-45 transition-colors duration-300 ${mat === m.id ? "bg-terra" : "bg-white/15"}`} />
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.3 }}
                className="mt-4 border border-white/5 bg-[#14171D] p-6"
              >
                <p className="text-sm leading-relaxed text-slate-400">{active.desc}</p>
                <ul className="mt-4 space-y-2">
                  {active.points.map((p) => (
                    <li key={p} className="flex items-center gap-2.5 text-xs text-slate-400">
                      <span className="h-1 w-1 rotate-45 bg-terra" />
                      {p}
                    </li>
                  ))}
                </ul>
                <a
                  href="#devis"
                  data-testid="mat3d-devis-cta"
                  className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-terra transition-colors hover:text-terra-hover"
                >
                  Demander ce matériau
                </a>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="relative min-w-0 border border-white/5 bg-[#0A0C0F]">
            <div className="absolute right-4 top-4 z-10 flex flex-wrap items-center justify-end gap-2">
              {mat !== "plat" && (
                <div className="flex rounded-full border border-white/15 bg-black/40 p-1 backdrop-blur">
                  <button
                    data-testid="mat3d-shape-hip"
                    onClick={() => setHip(true)}
                    className={`rounded-full px-3.5 py-1.5 text-[11px] font-semibold transition-colors duration-300 ${
                      hip ? "bg-terra text-white" : "text-slate-300 hover:text-white"
                    }`}
                  >
                    4 pans
                  </button>
                  <button
                    data-testid="mat3d-shape-gable"
                    onClick={() => setHip(false)}
                    className={`rounded-full px-3.5 py-1.5 text-[11px] font-semibold transition-colors duration-300 ${
                      !hip ? "bg-terra text-white" : "text-slate-300 hover:text-white"
                    }`}
                  >
                    2 pans
                  </button>
                </div>
              )}
              <button
                data-testid="mat3d-charpente-toggle"
                onClick={() => setFrame((f) => !f)}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold backdrop-blur transition-colors duration-300 ${
                  frame
                    ? "border-terra bg-terra text-white"
                    : "border-white/15 bg-black/40 text-slate-200 hover:border-terra/60"
                }`}
              >
                <Layers className="h-3.5 w-3.5" />
                {frame ? "Masquer la charpente" : "Voir la charpente"}
              </button>
            </div>
            <div className="pointer-events-none absolute bottom-4 left-4 z-10 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <MousePointer2 className="h-3 w-3" />
              Glissez pour explorer · rotation automatique
            </div>
            <div data-testid="mat3d-canvas" className="h-[280px] w-full sm:h-[340px] md:h-[420px]">
              <Canvas camera={{ position: [10.5, 5.2, 10.5], fov: 34 }} dpr={[1, 1.75]}>
                <ambientLight intensity={0.55} />
                <directionalLight position={[6, 9, 4]} intensity={1.3} />
                <directionalLight position={[-6, 4, -5]} intensity={0.55} color="#D96B43" />
                <directionalLight position={[-4, 7, -6]} intensity={0.5} color="#A8BFD4" />
                <RoofScene material={mat} showFrame={frame} hip={hip} progressRef={progressRef} />
                <ContactShadows position={[0, -1.45, 0]} opacity={0.55} scale={18} blur={2.4} far={5} />
                <OrbitControls
                  autoRotate
                  autoRotateSpeed={1.4}
                  enableZoom={false}
                  enablePan={false}
                  enableDamping
                  dampingFactor={0.08}
                  minPolarAngle={0.7}
                  maxPolarAngle={1.35}
                />
              </Canvas>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
